import React, { ChangeEvent, KeyboardEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box, Text, Input, FormLabel, FormControl } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { ResourceOption, ResourceType, SelectedResource } from "./SelectedFocusResources";
import { makeYoutubeEmbedUrl, isYoutubeVideoUrl } from "@/js/utils/youtubeUrlUtils";
import { getSignaturesForFocusResourcePicker, searchSignatures } from "./utils";
import { parseDomain } from "@/utils/urlUtils";
import { colours } from "@/utils/themeUtils";
import { useSignatureStore } from "@/stores/v2/signatureStore";

interface Props {
	addResource: (resource: SelectedResource) => void;
	lockedFocus: boolean;
}

export function FocusResourcePickerInput(props: Props): JSX.Element {
	const { t } = useTranslation();
	const [resourceInput, setResourceInput] = useState<string>("");
	const [resourceOptions, setResourceOptions] = useState<ResourceOption[]>([]);
	const [showOptionsDropDown, setShowOptionsDropDown] = useState<boolean>(false);

	const inputRef = useRef<HTMLInputElement>(null);
	const dropdownRef = useRef<HTMLDivElement>(null);
	const prevLockedFocusRef = useRef<boolean | undefined>(undefined);

	const signatures = useSignatureStore((state) => state.signatures);

	const availableSignatures = useMemo(() => {
		return getSignaturesForFocusResourcePicker(signatures);
	}, [signatures]);

	// Creates and cleans up an event listener for clicking outside the dropdown. This allows us to keep the dropdown open when the user clicks on options in it.
	useEffect(() => {
		document.addEventListener("mousedown", handleClickOutside);
		return () => {
			document.removeEventListener("mousedown", handleClickOutside);
		};
	}, []);

	function handleClickOutside(event: MouseEvent) {
		if (
			inputRef.current &&
			!inputRef.current.contains(event.target as Node) &&
			dropdownRef.current &&
			!dropdownRef.current.contains(event.target as Node)
		) {
			setShowOptionsDropDown(false);
		}
	}

	const createUrlResourceOption = useCallback(
		(url: string): ResourceOption => {
			let processedUrl = url;
			let label: string;
			if (isYoutubeVideoUrl(processedUrl)) {
				processedUrl = makeYoutubeEmbedUrl(processedUrl);
				label = t("focusLibrary.websiteLabelPrefix", { name: url });
			} else {
				label = t("focusLibrary.websiteLabelPrefix", { name: processedUrl });
			}

			return {
				label: label,
				resourceType: ResourceType.Url,
				resourceValue: processedUrl,
			};
		},
		[t]
	);

	function handleChangeResourceInput(event: ChangeEvent<HTMLInputElement>) {
		const val = event.target.value;

		setResourceInput(val);

		// if is a URL, only show one option
		if (parseDomain(val)) {
			const resourceOption = createUrlResourceOption(val);
			setResourceOptions([resourceOption]);
			return;
		}

		prepareSignatureOptions(availableSignatures, val);
	}

	function handleInputKeyDown(event: KeyboardEvent<HTMLInputElement>) {
		setShowOptionsDropDown(true);
		if (event.key === "Escape") {
			// Prevent the event from closing the drawer
			event.stopPropagation();

			// Manually unfocus the input and hide the dropdown
			(event.target as HTMLInputElement).blur();
			setShowOptionsDropDown(false);
		}

		if (event.key === "Enter") {
			event.preventDefault();
			if (resourceOptions.length == 1) {
				handleAddResource(resourceOptions[0]);
				setShowOptionsDropDown(false);
			}
		}
	}

	const prepareSignatureOptions = useCallback(
		(signatures: ResourceOption[], searchTerm?: string) => {
			if (!props.lockedFocus) {
				setResourceOptions(searchSignatures(signatures, searchTerm, 100));
			} else {
				setResourceOptions([]);
			}
		},
		[props.lockedFocus]
	);

	function handleAddResource(selected: ResourceOption): void {
		if (selected.resourceValue === "") return;

		setResourceInput("");
		prepareSignatureOptions(availableSignatures); // reset the options

		props.addResource({
			label: selected.resourceType === ResourceType.Url ? selected.resourceValue : selected.label,
			resourceType: selected.resourceType,
			resourceValue: selected.resourceValue,
			openTab: true,
			isValid: true,
			isLockedFocusCompatible: false,
		});
	}

	useEffect(() => {
		// Only run this effect when lockedFocus changes (or on initial mount)
		const lockedFocusChanged = prevLockedFocusRef.current !== undefined && prevLockedFocusRef.current !== props.lockedFocus;
		const isInitialMount = prevLockedFocusRef.current === undefined;

		if (lockedFocusChanged || isInitialMount) {
			prevLockedFocusRef.current = props.lockedFocus;
			// If there's a URL in the input, preserve it when lockedFocus changes
			if (resourceInput && parseDomain(resourceInput)) {
				const urlOption = createUrlResourceOption(resourceInput);
				setResourceOptions([urlOption]);
			} else {
				prepareSignatureOptions(availableSignatures);
			}
		}
	}, [prepareSignatureOptions, props.lockedFocus, availableSignatures, resourceInput, createUrlResourceOption]);

	return (
		<FormControl>
			<Box position={"relative"}>
				<FormLabel>{t("focusLibrary.resourcesLabel")}</FormLabel>
				<Input
					ref={inputRef}
					autoComplete="off"
					placeholder={props.lockedFocus ? t("focusLibrary.resourcePasteUrlLocked") : t("focusLibrary.resourcePasteUrlOrSearch")}
					value={resourceInput}
					onChange={handleChangeResourceInput}
					onClick={() => setShowOptionsDropDown(true)}
					data-testid="resource-input"
					onKeyDown={handleInputKeyDown}
				/>

				{showOptionsDropDown && (
					<Box
						ref={dropdownRef}
						background={"neutrals.0"}
						mt={2}
						position={"absolute"}
						width={"100%"}
						boxShadow={"md"}
						borderRadius={"md"}
						overflow={"scroll"}
						zIndex={10}
						maxHeight={"300px"}
					>
						{resourceOptions.length === 0 && (
							<Box color={"neutrals.300"} px={5} py={2}>
								{t("focusLibrary.noResultsFound")}
							</Box>
						)}

						{resourceOptions.map((option, index) => (
							<Box
								cursor={"pointer"}
								key={index}
								onClick={() => {
									handleAddResource(option);
									setShowOptionsDropDown(false);
								}}
								_hover={{
									background: colours.neutrals[50],
								}}
								transition={"background 0.2s"}
								px={5}
								py={2}
								borderBottom={1}
							>
								<Text fontSize={""}>{option.label}</Text>
							</Box>
						))}
					</Box>
				)}
			</Box>
		</FormControl>
	);
}
